// imports
import { OpenAIClient } from '@azure/openai'
import { AzureKeyCredential } from '@azure/core-auth'

// components
import BaseAdapter from '../base'

// utils
import utils from '@/utils/utils'
import { AdapterConfig, Chat, InternalMessage } from '@/utils/ai-adapters/xdc/types'
import messages from './messages'

type MessageRole = keyof typeof messages;

class OpenAIAdapter extends BaseAdapter {
    #instance: OpenAIClient | undefined
    #options
    chat: Chat[]
    updateChat?: (updatedChat: InternalMessage[]) => Promise<void>

    constructor(config: AdapterConfig) {
        super('OpenAIAdapter', config)

        this.#options = {}
        this.chat = []
    }

    async init(updateChat: (messageList: InternalMessage[]) => Promise<void> = async () => { }) {
        console.log(`[${this.name}] Initializing OpenAIAdapter with the following config:`, this.config);

        const credential = new AzureKeyCredential(this.apiKey ?? '')

        this.#instance = new OpenAIClient(this.endpoint ?? '', credential)
        this.updateChat = updateChat

        return
    }

    async getChatCompletions(messageList: unknown[], message: string | Chat, role: MessageRole | undefined) {
        let completion

        // second, update the chat with the message
        if (typeof message === 'string') {
            // first, parse message to include role and other necessary information
            const parsedMessage = this.parseMessage(role, message, {})
            this.updateNewChat(parsedMessage)
        } else {
            this.updateNewChat(message)
        }

        // third, send the message to OpenAI
        if (this.config?.asStream)
            completion = this.streamChatCompletions(this.chat)
        else
            completion = this.defaultChatCompletions(this.chat)
        await completion

        return
    }

    async defaultChatCompletions(chat: Chat[]) {
        const result = await this.#instance?.getChatCompletions(this.model ?? '', chat, this.#options)

        if (result?.choices) {
            for (const choice of result.choices)
                console.log(`[${this.name}] Received the following response:`, choice.message)
        }

        this.updateNewChat({
            role: result?.choices[0].message?.role ?? '',
            content: result?.choices[0].message?.content ?? null,
            ...result?.choices[0].message
        })

        return
    }

    async streamChatCompletions(chat: Chat[]) {

        console.log("chat list without filter", chat);
        let updatedChat = JSON.parse(JSON.stringify(chat)) // should only include parameters allowed by openai

        // use filter from utils here:
        updatedChat = utils.filterObjectArrayByParameters(chat, ['role', 'content'])
        console.log("chat list with filter", updatedChat);

        // if chatcompletion failes, set finishedMessage to true, to enable writing further messages
        let results
        try {
            results = await this.#instance?.streamChatCompletions(this.model ?? '', updatedChat, this.#options)
        } catch {
            const updatedChat = [...this.chat]
            updatedChat[updatedChat.length - 1] = { ... this.chat[this.chat.length - 1], finishedMessage: true }
            await this.updateNewChat(updatedChat, true)
        }

        if (results) {
            for await (const event of results) {
                for (const choice of event.choices) {
                    switch (true) {
                        case !!choice.delta?.role: {
                            const parsedMessage: Chat = this.parseMessage(choice.delta.role as MessageRole, '', choice.delta)
                            // console.log("parsedMessage 1", parsedMessage);
                            parsedMessage.finishedMessage = false
                            // console.log("parsedMessage 2", parsedMessage);

                            this.updateNewChat(parsedMessage, false)
                            break
                        }
                        case !!choice.delta?.content: {
                            const updatedChat = [...this.chat]
                            const parsedMessage = { ...updatedChat[this.chat.length - 1] }

                            parsedMessage.content += choice.delta.content

                            // console.log("parsedMessage 3", parsedMessage);
                            parsedMessage.finishedMessage = false  // add finishedMessage lable, such that chat-components knows, when the message wont update anymore
                            // console.log("parsedMessage 4", parsedMessage);

                            updatedChat[updatedChat.length - 1] = parsedMessage
                            await this.updateNewChat(updatedChat, true)
                            break
                        }
                        case choice.finishReason === 'stop': {
                            //  flag last message as finished
                            const updatedChat = [...this.chat]
                            updatedChat[updatedChat.length - 1] = { ... this.chat[this.chat.length - 1], finishedMessage: true }
                            await this.updateNewChat(updatedChat, true)

                            console.log('streaming done')
                            console.log("updatedChat finished :D", updatedChat);
                            break
                        }
                        default: {
                            console.log('choice not recognized', choice)
                        }
                    }
                }
            }
        }

        return
    }

    // async getAudioTranscription(audio: Blob, params: Record<string, unknown>) {
    //     // console.log(`[${this.name}] Sending the following audio:`, audio)
    //     // const result = await this.#instance?.getAudioTranscription('whisper', audio)

    //     // return result
    // }

    /*************************************************************************************************************
     *                                        helper functions
     ************************************************************************************************************/

    parseMessage(role: MessageRole | undefined, message: string, args: object): Chat & { content: string } { // moved to assistant class
        const parsedMessage = { ...(role ? messages[role] : {}), ...args }

        parsedMessage.content = message ?? '';

        return parsedMessage as Chat & { content: string }
    }

    async updateNewChat(message: Chat | Chat[], replaceChat = false) {
        let updatedChat = JSON.parse(JSON.stringify(this.chat))

        if (Array.isArray(message)) {
            if (replaceChat)
                updatedChat = JSON.parse(JSON.stringify(message))
            else
                updatedChat.push(...message)
        }
        else
            updatedChat.push(message)

        this.chat = updatedChat
        if (typeof this.updateChat === 'function') {
            await this.updateChat(updatedChat)
        }
    }

    updateNewChatWithDelay(message: Chat | Chat[], delay: number) {
        setTimeout(() => {
            this.updateNewChat(message, true)
        }, delay)
    }
}

export default OpenAIAdapter